import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from './Layout';
import { SellerLayout } from './SellerLayout';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { colors } from '../constants/colors';
import { getMySellerShows, getSalesBySeller, getSellerBalanceSummary, getOrdersBySeller } from '../api/client';
import { parseUTC } from '../utils/date';
import type { Show, ItemSaleWithPayment, SellerBalanceSummary } from '../types';

export function SellerHub() {
  useDocumentTitle('Seller Hub');
  const navigate = useNavigate();
  const { user, enrollSeller, submitSellerInterest } = useAuth();
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [interestNote, setInterestNote] = useState('');
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);
  const [upcomingShows, setUpcomingShows] = useState<Show[]>([]);
  const [showsLoading, setShowsLoading] = useState(true);
  const [orders, setOrders] = useState<ItemSaleWithPayment[]>([]);
  const [balance, setBalance] = useState<SellerBalanceSummary | null>(null);
  const [unshippedCount, setUnshippedCount] = useState(0);
  const [statsLoading, setStatsLoading] = useState(true);
  const [liveShow, setLiveShow] = useState<Show | null>(null);

  // Load upcoming shows and stats
  useEffect(() => {
    const loadData = async () => {
      try {
        setShowsLoading(true);
        setStatsLoading(true);

        // Load shows, orders, and balance in parallel
        const [myShows, sellerOrders, balanceData, unshippedData] = await Promise.all([
          getMySellerShows(),
          getSalesBySeller().catch(() => []),
          getSellerBalanceSummary().catch(() => null),
          getOrdersBySeller(0, 1, 'UNSHIPPED').catch(() => ({ totalElements: 0 })),
        ]);

        // Detect any currently live show
        const live = myShows.find(s => s.status === 'LIVE');
        setLiveShow(live || null);

        // Filter to scheduled shows and sort by scheduledAt ascending (soonest first)
        const scheduled = myShows.filter(s => s.status === 'SCHEDULED');
        scheduled.sort((a, b) => parseUTC(a.scheduledAt).getTime() - parseUTC(b.scheduledAt).getTime());
        setUpcomingShows(scheduled);
        setOrders(sellerOrders);
        setBalance(balanceData);
        setUnshippedCount(unshippedData.totalElements);
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setShowsLoading(false);
        setStatsLoading(false);
      }
    };

    if (user?.isSeller) {
      loadData();
    }
  }, [user?.isSeller]);

  const handleEnrollSeller = async () => {
    setIsEnrolling(true);
    setEnrollError(null);
    try {
      await enrollSeller();
    } catch (err) {
      setEnrollError(err instanceof Error ? err.message : 'Failed to enroll as seller');
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleSubmitInterest = async () => {
    if (!interestNote.trim()) return;
    setIsSubmittingInterest(true);
    setEnrollError(null);
    try {
      await submitSellerInterest(interestNote.trim());
    } catch (err) {
      setEnrollError(err instanceof Error ? err.message : 'Failed to submit interest');
    } finally {
      setIsSubmittingInterest(false);
    }
  };

  // Show seller enrollment screen if user is not a seller
  if (!user?.isSeller) {
    // STATE C: Interest submitted, waiting for approval
    if (!user?.sellerWhitelisted && user?.sellerInterestSubmittedAt) {
      return (
        <Layout>
          <div style={enrollStyles.enrollContainer}>
            <div style={enrollStyles.enrollCard}>
              <div style={{ ...enrollStyles.iconWrapper, backgroundColor: '#dcfce7' }}>
                <CheckCircleIcon />
              </div>
              <h2 style={enrollStyles.enrollTitle}>Application Submitted</h2>
              <p style={enrollStyles.enrollDescription}>
                We'll review your application and let you know when you're approved to start selling.
              </p>
            </div>
          </div>
        </Layout>
      );
    }

    // STATE B: Not whitelisted, show interest form
    if (!user?.sellerWhitelisted) {
      return (
        <Layout>
          <div style={enrollStyles.enrollContainer}>
            <div style={enrollStyles.enrollCard}>
              <div style={enrollStyles.iconWrapper}>
                <ShopIcon />
              </div>
              <h2 style={enrollStyles.enrollTitle}>Interested in Selling?</h2>
              <p style={enrollStyles.enrollDescription}>
                Snagg lets you reach buyers in real-time through live streaming auctions.
                Tell us a bit about what you'd like to sell.
              </p>
              <ul style={enrollStyles.benefitsList}>
                <li style={enrollStyles.benefitItem}>
                  <CheckIcon /> Go live and reach buyers in real-time
                </li>
                <li style={enrollStyles.benefitItem}>
                  <CheckIcon /> Set your own prices and auction rules
                </li>
                <li style={enrollStyles.benefitItem}>
                  <CheckIcon /> Get paid quickly and securely
                </li>
              </ul>
              <textarea
                placeholder="Tell us about what you'd like to sell..."
                value={interestNote}
                onChange={e => setInterestNote(e.target.value)}
                maxLength={1000}
                rows={4}
                style={enrollStyles.textarea}
              />
              <div style={enrollStyles.charCount}>{interestNote.length}/1000</div>
              {enrollError && <div style={enrollStyles.error}>{enrollError}</div>}
              <button
                onClick={handleSubmitInterest}
                style={{
                  ...enrollStyles.enrollButton,
                  opacity: interestNote.trim() ? 1 : 0.5,
                  cursor: interestNote.trim() ? 'pointer' : 'not-allowed',
                }}
                disabled={isSubmittingInterest || !interestNote.trim()}
              >
                {isSubmittingInterest ? 'Submitting...' : 'Submit Interest'}
              </button>
            </div>
          </div>
        </Layout>
      );
    }

    // STATE A: Whitelisted, show enrollment card
    return (
      <Layout>
        <div style={enrollStyles.enrollContainer}>
          <div style={enrollStyles.enrollCard}>
            <div style={enrollStyles.iconWrapper}>
              <ShopIcon />
            </div>
            <h2 style={enrollStyles.enrollTitle}>Start Selling on Snagg</h2>
            <p style={enrollStyles.enrollDescription}>
              You've been approved to sell! Set up your store and start selling today.
            </p>
            <ul style={enrollStyles.benefitsList}>
              <li style={enrollStyles.benefitItem}>
                <CheckIcon /> Go live and reach buyers in real-time
              </li>
              <li style={enrollStyles.benefitItem}>
                <CheckIcon /> Set your own prices and auction rules
              </li>
              <li style={enrollStyles.benefitItem}>
                <CheckIcon /> Get paid quickly and securely
              </li>
            </ul>
            {enrollError && <div style={enrollStyles.error}>{enrollError}</div>}
            <button
              onClick={handleEnrollSeller}
              style={enrollStyles.enrollButton}
              disabled={isEnrolling}
            >
              {isEnrolling ? 'Setting up...' : 'Become a Seller'}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const formatScheduledTime = (scheduledAt: string): string => {
    const date = parseUTC(scheduledAt);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `In ${diffDays} day${diffDays > 1 ? 's' : ''}`;
    } else if (diffHours > 0) {
      return `In ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    } else {
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return diffMins > 0 ? `In ${diffMins} min` : 'Start now';
    }
  };

  const handleGoLive = () => {
    navigate('/sell/live');
  };

  return (
    <SellerLayout>
      <main style={styles.main}>
        {/* Left Column */}
        <div style={styles.leftColumn}>
          {/* Overview Stats */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Stats</h2>
            <div style={styles.statsGrid}>
              <div style={styles.statCard} onClick={() => navigate('/sell/orders?status=UNSHIPPED')}>
                <div style={styles.statIconWrapper}>
                  <PackageIcon color={colors.brand.blue} />
                </div>
                <div style={styles.statContent}>
                  <span style={styles.statLabel}>Orders</span>
                  <div style={styles.statValueRow}>
                    <span style={styles.statValue}>
                      {statsLoading ? '...' : unshippedCount.toLocaleString()}
                    </span>
                    {!statsLoading && unshippedCount > 0 && (
                      <span style={styles.badge}>Action needed</span>
                    )}
                  </div>
                  <span style={styles.statSubtext}>
                    {statsLoading
                      ? 'Loading...'
                      : unshippedCount > 0
                        ? `${unshippedCount.toLocaleString()} order${unshippedCount !== 1 ? 's' : ''} need${unshippedCount === 1 ? 's' : ''} shipping`
                        : 'All shipped'}
                  </span>
                </div>
                <ChevronIcon />
              </div>

              <div style={styles.statCard} onClick={() => navigate('/sell/payouts')}>
                <div style={styles.statIconWrapper}>
                  <DollarIcon color={colors.brand.blue} />
                </div>
                <div style={styles.statContent}>
                  <span style={styles.statLabel}>Available Balance</span>
                  <span style={styles.statValue}>
                    {statsLoading
                      ? '...'
                      : balance?.availableBalance != null
                        ? `$${Number(balance.availableBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '$0.00'}
                  </span>
                  <span style={styles.statSubtext}>View payouts</span>
                </div>
                <ChevronIcon />
              </div>

              <div style={styles.statCard} onClick={() => navigate('/sell/orders')}>
                <div style={styles.statIconWrapper}>
                  <TrendingIcon color={colors.brand.blue} />
                </div>
                <div style={styles.statContent}>
                  <span style={styles.statLabel}>Total Sales</span>
                  <span style={styles.statValue}>
                    {statsLoading
                      ? '...'
                      : balance?.lifetimeEarnings != null
                        ? `$${Number(balance.lifetimeEarnings).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '$0.00'}
                  </span>
                  <span style={styles.statSubtext}>
                    {statsLoading ? 'Loading...' : `${orders.length.toLocaleString()} sales`}
                  </span>
                </div>
                <ChevronIcon />
              </div>
            </div>
          </section>
        </div>

        {/* Right Column */}
        <div style={styles.rightColumn}>
          {/* Quick Actions */}
          <section style={styles.section}>
            {liveShow && (
              <div style={styles.liveBanner}>
                <div style={styles.liveBannerHeader}>
                  <div style={styles.liveDot} />
                  <span style={styles.liveBannerLabel}>Currently streaming on another device</span>
                </div>
                <span style={styles.liveBannerShowName}>{liveShow.name}</span>
              </div>
            )}
            <div style={styles.quickActionsRow}>
              {liveShow ? (
                <button onClick={() => navigate(`/sell/live/${liveShow.id}`)} style={styles.joinNowButton}>
                  <div style={{ ...styles.actionIconWrapper, backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <VideoIcon />
                  </div>
                  <span>Join Now</span>
                </button>
              ) : (
                <button onClick={handleGoLive} style={styles.goLiveButton}>
                  <div style={styles.actionIconWrapper}>
                    <VideoIcon />
                  </div>
                  <span>Go Live</span>
                </button>
              )}
              <button style={styles.actionButton} onClick={() => navigate('/sell/live?mode=schedule')}>
                <div style={{ ...styles.actionIconWrapperLight, backgroundColor: '#EFF6FF' }}>
                  <CalendarIcon color="#3B82F6" />
                </div>
                <span>Schedule Show</span>
              </button>
            </div>
          </section>

          {/* Upcoming Shows */}
          <section style={styles.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ ...styles.sectionTitle, margin: 0 }}>Upcoming Shows</h2>
              <button style={styles.seeAllLink} onClick={() => navigate('/sell/shows')}>See all</button>
            </div>
            <div style={styles.showsList}>
              {showsLoading ? (
                <div style={styles.showsLoading}>Loading shows...</div>
              ) : upcomingShows.length === 0 ? (
                <div style={styles.noShows}>
                  <p style={styles.noShowsText}>No upcoming shows scheduled</p>
                  <button
                    style={styles.createShowButton}
                    onClick={() => navigate('/sell/live')}
                  >
                    Create a Show
                  </button>
                </div>
              ) : (
                upcomingShows.map((show) => (
                  <div
                    key={show.id}
                    style={{ ...styles.showCard, cursor: 'pointer' }}
                    onClick={() => navigate(`/sell/shows/${show.id}`)}
                  >
                    <div style={styles.showContent}>
                      <span style={styles.showName}>{show.name}</span>
                      <span style={styles.showTime}>{formatScheduledTime(show.scheduledAt)}</span>
                      {show.categories && show.categories.length > 0 && (
                        <div style={styles.showCategories}>
                          {show.categories.map((cat, i) => (
                            <span key={i} style={styles.categoryTag}>{cat}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      style={styles.startButton}
                      onClick={(e) => { e.stopPropagation(); navigate(`/sell/live/${show.id}`); }}
                    >
                      Start
                    </button>
                  </div>
                ))
              )}
            </div>
          </section>

        </div>
      </main>
    </SellerLayout>
  );
}

// Simple SVG Icons
function VideoIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="23 7 16 12 23 17 23 7" />
      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
    </svg>
  );
}

function CalendarIcon({ color = '#000' }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function PackageIcon({ color = '#000' }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

function TrendingIcon({ color = '#000' }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}

function DollarIcon({ color = '#000' }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={colors.gray[400]} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function ShopIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={colors.brand.blue} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CheckCircleIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  main: {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '24px',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  section: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '20px',
    border: `1px solid ${colors.gray[300]}`,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700,
    margin: '0 0 16px 0',
    color: colors.ink.black,
  },
  seeAllLink: {
    background: 'none',
    border: 'none',
    color: colors.brand.blue,
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
  },
  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  quickActionsRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px',
  },
  liveBanner: {
    backgroundColor: '#F9FAFB',
    border: `1px solid ${colors.gray[200]}`,
    borderRadius: '12px',
    padding: '14px 16px',
    marginBottom: '12px',
  } as React.CSSProperties,
  liveBannerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  } as React.CSSProperties,
  liveDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: colors.status.success,
  } as React.CSSProperties,
  liveBannerLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: colors.gray[600],
  } as React.CSSProperties,
  liveBannerShowName: {
    fontSize: '15px',
    fontWeight: 600,
    color: colors.ink.black,
  } as React.CSSProperties,
  joinNowButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: colors.accent.purple,
    color: colors.white,
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'transform 0.1s, box-shadow 0.1s',
  } as React.CSSProperties,
  goLiveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: colors.accent.pink,
    color: colors.white,
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'transform 0.1s, box-shadow 0.1s',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: '#F9FAFB',
    color: colors.ink.black,
    border: 'none',
    borderRadius: '12px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.1s',
  },
  actionIconWrapper: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    backgroundColor: 'rgba(255,255,255,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionIconWrapperLight: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    borderRadius: '12px',
    cursor: 'pointer',
  },
  statIconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: colors.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  statLabel: {
    fontSize: '12px',
    color: colors.gray[600],
  },
  statValueRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statValue: {
    fontSize: '18px',
    fontWeight: 700,
    color: colors.ink.black,
  },
  statSubtext: {
    fontSize: '12px',
    color: colors.brand.blue,
  },
  badge: {
    padding: '2px 8px',
    backgroundColor: colors.accent.pink,
    color: colors.white,
    fontSize: '10px',
    fontWeight: 600,
    borderRadius: '9999px',
  },
  showsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  showCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    borderRadius: '12px',
  },
  showContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  showName: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
  },
  showTime: {
    fontSize: '13px',
    color: colors.gray[600],
    marginTop: '4px',
  },
  showCategories: {
    display: 'flex',
    gap: '6px',
    marginTop: '8px',
  },
  categoryTag: {
    padding: '4px 10px',
    backgroundColor: colors.white,
    borderRadius: '9999px',
    fontSize: '12px',
    color: colors.gray[600],
  },
  startButton: {
    padding: '10px 20px',
    backgroundColor: colors.accent.pink,
    color: colors.white,
    border: 'none',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  showsLoading: {
    padding: '24px',
    textAlign: 'center',
    color: colors.gray[600],
    fontSize: '14px',
  },
  noShows: {
    padding: '24px',
    textAlign: 'center',
  },
  noShowsText: {
    color: colors.gray[600],
    fontSize: '14px',
    margin: '0 0 16px 0',
  },
  createShowButton: {
    padding: '10px 20px',
    backgroundColor: colors.brand.blue,
    color: colors.white,
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

const enrollStyles: Record<string, React.CSSProperties> = {
  enrollContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'calc(100vh - 80px)',
    padding: '24px',
  },
  enrollCard: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '48px',
    maxWidth: '500px',
    textAlign: 'center',
    border: `1px solid ${colors.gray[300]}`,
  },
  iconWrapper: {
    width: '80px',
    height: '80px',
    borderRadius: '20px',
    backgroundColor: colors.brand.blue15,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  enrollTitle: {
    fontSize: '24px',
    fontWeight: 700,
    color: colors.ink.black,
    marginBottom: '12px',
  },
  enrollDescription: {
    fontSize: '15px',
    color: colors.gray[600],
    lineHeight: 1.6,
    marginBottom: '24px',
  },
  benefitsList: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 24px 0',
    textAlign: 'left',
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    borderBottom: `1px solid ${colors.gray[100]}`,
    fontSize: '14px',
    color: colors.gray[900],
  },
  enrollButton: {
    width: '100%',
    padding: '16px',
    backgroundColor: colors.brand.blue,
    color: colors.white,
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '12px',
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '8px',
    fontSize: '14px',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
    marginBottom: '4px',
    outline: 'none',
  },
  charCount: {
    fontSize: '12px',
    color: colors.gray[400],
    textAlign: 'right' as const,
    marginBottom: '16px',
  },
  error: {
    backgroundColor: `${colors.accent.pink}15`,
    border: `1px solid ${colors.accent.pink}40`,
    borderRadius: '8px',
    padding: '12px',
    color: colors.accent.pink,
    fontSize: '14px',
    marginBottom: '16px',
  },
};
